import React from 'react';
 
import Button from './Button';
 
import Icon from '../AppIcon';

/**
 * Info modal component for displaying information messages
 * @param {Object} props
 * @param {string} props.title - Modal title
 * @param {string} props.message - Modal message
 * @param {string} props.buttonLabel - Label for ok button
 * @param {Function} props.buttonAction - Function to call when button is clicked
 * @param {Function} props.onClose - Function to call when closing
 * @param {string} props.type - Modal type (info, success, warning, error)
 */
const InfoModal = ({
  title = 'Information',
  message = '',
  buttonLabel = 'OK',
  buttonAction,
  onClose,
  type = 'info'
}) => {
  const getIconName = () => {
    switch (type) {
      case 'error': return 'XCircle';
      case 'warning': return 'AlertCircle';
      case 'success': return 'CheckCircle';
      case 'info':
      default: return 'Info';
    }
  };
  
  const getIconColor = () => {
    switch (type) {
      case 'error': return 'text-error';
      case 'warning': return 'text-warning';
      case 'success': return 'text-success';
      case 'info':
      default: return 'text-info';
    }
  };
  
  const getButtonVariant = () => {
    switch (type) {
      case 'error': return 'danger';
      case 'warning': return 'primary'; // Use primary for better visibility
      case 'success': return 'primary'; // Use primary for better visibility
      case 'info':
      default: return 'primary';
    }
  };
  
  // Handle escape key press
  React.useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onClose]);
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full animate-fade-in">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-xl font-semibold text-text-primary">{title}</h3>
          </div>
          
          <p className="text-text-secondary mb-6 whitespace-pre-line">{message}</p>
          
          <div className="flex justify-end">
            <Button
              variant={getButtonVariant()}
              onClick={async () => {
                if (!buttonAction) {
                  onClose();
                  return;
                }

                try {
                  const result = buttonAction();

                  // Allow buttonAction to keep the modal open by returning false
                  if (result && typeof result.then === 'function') {
                    const resolved = await result;
                    if (resolved === false) {
                      return;
                    }
                    onClose();
                  } else {
                    if (result === false) {
                      return;
                    }
                    onClose();
                  }
                } catch (error) {
                  // On error, fall back to closing the modal to avoid trapping the user
                  onClose();
                }
              }}
            >
              {buttonLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoModal;
